import { CREATOR } from "../config";

/**
 * Static legal/security page content. Authored by us (trusted), rendered via
 * innerHTML into the `.legal` container. No user input is interpolated here.
 */

const UPDATED = "June 2026";

const creatorLine = `Built by <a href="${CREATOR.portfolio}" target="_blank" rel="noopener">${CREATOR.name}</a> · <a href="${CREATOR.linkedin}" target="_blank" rel="noopener">LinkedIn</a>`;

export function termsHtml(): string {
  return `
    <a class="back" href="#/">&larr; Back to Enclave</a>
    <h1>Terms of Service</h1>
    <p class="muted">Last updated: ${UPDATED}</p>

    <p>Enclave is a free, end-to-end encrypted, peer-to-peer file transfer tool.
    By using it you agree to these terms.</p>

    <h2>1. The service</h2>
    <p>Enclave generates a single-use link that lets two people open a direct,
    encrypted connection between their browsers and transfer files. We operate a
    signaling relay that only helps the two browsers find each other; it cannot
    read your files and stores nothing.</p>

    <h2>2. Acceptable use</h2>
    <p>You agree not to use Enclave to transfer unlawful content, to infringe
    others' rights, to distribute malware, or to violate any applicable law.
    Because transfers are end-to-end encrypted and never stored, <strong>you are
    solely responsible</strong> for the content you send and receive and for the
    people you share links with.</p>

    <h2>3. No accounts, no storage</h2>
    <p>Enclave has no accounts and keeps no copy of your data. A connection and
    everything in it ceases to exist when the transfer ends or the tab closes.</p>

    <h2>4. No warranty</h2>
    <p>Enclave is provided "as is", without warranty of any kind. Direct
    peer-to-peer connections are not always possible (for example on some
    restrictive networks), and delivery is not guaranteed.</p>

    <h2>5. Limitation of liability</h2>
    <p>To the maximum extent permitted by law, the creator shall not be liable for
    any damages arising from your use of, or inability to use, Enclave.</p>

    <h2>6. Changes</h2>
    <p>These terms may change over time. Continued use after changes constitutes
    acceptance.</p>

    <h2>Contact</h2>
    <p>${creatorLine}</p>
  `;
}

export function privacyHtml(): string {
  return `
    <a class="back" href="#/">&larr; Back to Enclave</a>
    <h1>Privacy Policy</h1>
    <p class="muted">Last updated: ${UPDATED}</p>

    <p>Enclave is designed to know as little as technically possible. There are no
    accounts, no analytics, no cookies, and no file storage.</p>

    <h2>What we cannot see</h2>
    <p>Your files and their names are <strong>end-to-end encrypted</strong> in your
    browser and travel directly between the two peers. The secret that protects
    them lives only in the link's <code>#fragment</code>, which browsers never send
    to a server. We cannot read, recover, or hand over your data — we never have it.</p>

    <h2>What the signaling relay briefly handles</h2>
    <p>To introduce two browsers, our relay momentarily processes:</p>
    <ul>
      <li>a high-entropy <code>roomId</code> (a one-way hash of your link secret);</li>
      <li>IP addresses and connection timing, inherent to establishing any network connection;</li>
      <li>opaque encrypted setup blobs (SDP/ICE) it forwards without understanding.</li>
    </ul>
    <p>This is held in memory only for the seconds it takes to connect the two
    sides, then discarded. Nothing is written to a database or logs of content.</p>

    <h2>On your device</h2>
    <p>We store small non-sensitive preferences (theme, auto-accept) in your
    browser's <code>localStorage</code>. Encryption keys and file data are never
    persisted — they exist in memory only and are gone when the tab closes.</p>

    <h2>Third parties</h2>
    <p>Transfers go directly peer-to-peer. We do not use a TURN relay, advertising,
    or third-party trackers. STUN servers are used only to discover your public
    address for the direct connection and never carry your data.</p>

    <h2>Children</h2>
    <p>Enclave is not directed to children under 13.</p>

    <h2>Contact</h2>
    <p>${creatorLine}</p>
  `;
}

export function securityHtml(): string {
  return `
    <a class="back" href="#/">&larr; Back to Enclave</a>
    <h1>Security</h1>
    <p class="muted">Last updated: ${UPDATED}</p>

    <p>Enclave is built so that you do not have to trust its server. Security comes
    from cryptography in your browser, not from our infrastructure.</p>

    <h2>How it works</h2>
    <ul>
      <li><strong>Secret in the link.</strong> Your browser generates a 256-bit
      token, kept in the link's <code>#fragment</code> and never sent to the server.</li>
      <li><strong>Authenticated key exchange.</strong> The two peers run a
      PSK-authenticated ephemeral X25519 handshake (the Noise <code>NNpsk0</code>
      pattern) over the data channel. The token-derived pre-shared key proves both
      sides hold the same link and stops a server or network from sitting in the
      middle — the handshake fails closed if tampered.</li>
      <li><strong>End-to-end encryption.</strong> Files are sealed with
      XChaCha20-Poly1305 using keys from the handshake, layered above WebRTC's own
      DTLS. The signaling relay only ever sees ciphertext.</li>
      <li><strong>Forward secrecy.</strong> Ephemeral keys mean a leaked link later
      cannot decrypt a past transfer.</li>
      <li><strong>Ephemeral by design.</strong> One-time links, no accounts, no
      storage. Connections end after the transfer.</li>
    </ul>

    <h2>Verify your peer (optional)</h2>
    <p>This is a manual, out-of-band check, so it is optional. The
    PSK-authenticated handshake already stops the <em>server</em> or network from
    getting in the middle automatically. Comparing the on-screen code with the
    other person in person or over a call adds a second layer that also catches an
    intercepted <em>link</em> — worth doing for sensitive transfers, fine to skip
    otherwise.</p>

    <h2>Known limitations</h2>
    <ul>
      <li>The link is a bearer capability — anyone who obtains it can connect, so
      share it over a channel you trust (or scan in person).</li>
      <li>Metadata (IP addresses, timing, sizes) is visible to the network during
      connection setup; only content is end-to-end encrypted.</li>
    </ul>

    <h2>Responsible disclosure</h2>
    <p>Found a vulnerability? Please report it privately via
    <a href="${CREATOR.linkedin}" target="_blank" rel="noopener">LinkedIn</a> or the
    contact form at <a href="${CREATOR.portfolio}" target="_blank" rel="noopener">deb0.com</a>
    before disclosing publicly. See the project's <code>SECURITY.md</code> for details.</p>

    <h2>Contact</h2>
    <p>${creatorLine}</p>
  `;
}
